// Helpers of network runs on the run pages and lists (feature spec 17, phase 1; spec 11 6.15.11, 6.15.17): which runs
// are network runs, the labels of network components in plans, their dangerous changes, the grant-pending retry words.
import type {
  Attachment,
  ChangeAction,
  PlanChange,
  PlannedNetworkRequest,
  Run,
  RunChanges,
  RunSummary,
  SharedChange,
} from '../../api/types';
import { formatClock } from '../../lib/format';
import { strings } from '../../strings';
import { providerLabel } from '../shared';

const r = strings.networks.runs;

/** A run of a network (feature spec 17): it has a network and no deployment. */
export function isNetworkSummary(run: Pick<RunSummary | Run, 'networkId' | 'deploymentId'>): boolean {
  return !!run.networkId && !run.deploymentId;
}

/** Deployment names by ID, for component labels (the network's attachments and the page's own data). */
export type DeploymentNames = ReadonlyMap<string, string>;

export function namesFrom(
  attachments: readonly Pick<Attachment, 'deploymentId' | 'deploymentName'>[] | undefined,
  extra: readonly [string | null | undefined, string | null | undefined][] = [],
): Map<string, string> {
  const names = new Map<string, string>();
  for (const a of attachments ?? []) names.set(a.deploymentId, a.deploymentName);
  for (const [id, name] of extra) if (id && name) names.set(id, name);
  return names;
}

/**
 * A component key as the Resource column reads it (6.15.11): "<deployment> · azure / westeurope" for an attachment,
 * "azure · westeurope" for a region, "azure" for the zones, else the key.
 */
export function componentLabel(key: string, names: DeploymentNames = new Map()): string {
  const parts = key.split('/');
  switch (parts[0]) {
    case 'network.attachment':
      return parts.length >= 4
        ? r.attachmentResource(
            names.get(parts[1]) ?? strings.networks.runLabels.aDeployment,
            parts[2],
            parts[3],
          )
        : key;
    case 'network.region':
      return parts.length >= 3 ? r.regionResource(parts[1], parts[2]) : key;
    case 'network.zones':
      return parts[1] ?? key;
    case 'network.entry-point':
      return parts[1] ? r.entryPointResource(parts[1]) : key;
    default:
      return key;
  }
}

/** The Type column of a component: "Attachment", "Region", "Private DNS zones"; unknown types by name. */
export function componentTypeLabel(type: string): string {
  return r.componentTypes[type] ?? type;
}

/** Whether a plan lists network components (a network run's plan). */
export function isComponentPlan(changes: readonly PlanChange[]): boolean {
  return changes.some((c) => c.type.startsWith('network.'));
}

/**
 * A component's replace or delete that interrupts traffic (`outage`), or an entry point's delete (feature spec 17,
 * 17.9): listed first, needs the typed name.
 */
export function isOutage(change: Pick<PlanChange, 'action' | 'type' | 'replaceImpacts'>): boolean {
  if (change.action !== 'replace' && change.action !== 'delete') return false;
  return (
    (change.replaceImpacts ?? []).includes('outage') ||
    (change.action === 'delete' && change.type === 'network.entry-point')
  );
}

/** An entry point that keeps existing but gets a new host name or IPs (`endpoint-changes`, phase 3). */
export function changesEndpoint(change: Pick<PlanChange, 'action' | 'replaceImpacts'>): boolean {
  return (
    (change.action === 'replace' || change.action === 'update') &&
    (change.replaceImpacts ?? []).includes('endpoint-changes')
  );
}

/** "<component> is re-created: traffic through it stops briefly." / "… is deleted: traffic through it stops." */
export function outageSentence(change: PlanChange, names: DeploymentNames): string {
  const label = componentLabel(change.resourceId, names);
  return change.action === 'delete' ? r.outageDeleted(label) : r.outageReplaced(label);
}

/** Network components in review order: outage changes first, then the plan's order. */
export function componentOrder(changes: readonly PlanChange[]): PlanChange[] {
  return [...changes]
    .map((c, i) => ({ c, i }))
    .sort((a, b) => Number(isOutage(b.c)) - Number(isOutage(a.c)) || a.i - b.i)
    .map((x) => x.c);
}

/** "retrying at 10:42 (waiting for permissions)" or "retrying soon (waiting for permissions)". */
export function retryWords(until: string | null | undefined): string {
  return until ? r.retrying(formatClock(until)) : r.retryingSoon;
}

/** "azure / westeurope, northeurope" of a deployment network's attachments. */
export function regionsLine(attachments: readonly { provider: string; region: string }[]): {
  provider: string;
  regions: string;
} {
  const providers = [...new Set(attachments.map((a) => a.provider))];
  return {
    provider: providers.map(providerLabel).join(', ').toLowerCase(),
    regions: [...new Set(attachments.map((a) => a.region))].join(', '),
  };
}

/** The connect run of `deployment` created after `apply` started (the newest one: a retry replaces it). */
export function connectRunAfter(
  runs: readonly Run[],
  deploymentId: string,
  apply: Pick<Run, 'startedAt' | 'createdAt'>,
) {
  const since = Date.parse(apply.startedAt ?? apply.createdAt);
  return (
    runs.find(
      (x) =>
        x.scope === 'attachment' &&
        x.attachmentMode === 'connect' &&
        x.attachmentDeploymentId === deploymentId &&
        Date.parse(x.createdAt) >= since,
    ) ?? null
  );
}

/** The spoke group's changes as `RunChanges` (when the run has no stored `networkChanges`). */
export function spokeCounts(spoke: readonly SharedChange[]): RunChanges {
  const count = (action: ChangeAction) => spoke.filter((c) => c.action === action).length;
  return {
    create: count('create'),
    update: count('update'),
    replace: count('replace'),
    delete: count('delete'),
  };
}

// ---- Feature spec 17, phase 2: a plan's network requests (6.15.23)

const rq = strings.networks.requests;

/** The requests a plan's apply closes on an enforcing network (U4.1), in words. */
export function closingRequests(requests: readonly PlannedNetworkRequest[]): string[] {
  return requests
    .filter((x) => x.closesOnApply)
    .map((x) =>
      x.change === 'removed'
        ? rq.closesRemoved(
            x.requestId,
            x.sourceResourceId,
            `${x.target?.name ?? ''}/${x.target?.resourceId ?? ''}`,
          )
        : rq.closesChanged(
            x.requestId,
            x.sourceResourceId,
            x.target?.name ?? '',
            x.target?.resourceId ?? '',
            x.networkName,
          ),
    );
}

/** Requests that stay blocked until a network admin approves them (enforcing networks only). */
export function blockedRequests(requests: readonly PlannedNetworkRequest[]): PlannedNetworkRequest[] {
  return requests.filter(
    (x) => x.enforcing && x.change !== 'removed' && (x.proposedDecision ?? x.appliedDecision) === 'pending',
  );
}
